/**
 * Which boards a school teaches — the ticks on the platform console's "Add a
 * school" form and on its "Edit school" panel, one component so the two offer
 * exactly the same choice.
 *
 * The list is data, read live from `/curriculum/levels`: a board a platform
 * operator adds in the curriculum console appears here on its next fetch.
 */
import type { Board } from '@cwy/config/contracts';
import { useCurriculumLevels } from '@/hooks/useCurriculumLevels';

export function BoardPicker({
  value,
  onChange,
  disabled,
}: {
  value: ReadonlySet<Board>;
  onChange: (next: Set<Board>) => void;
  disabled?: boolean;
}) {
  const levels = useCurriculumLevels();

  if (levels.isPending) return <p className="text-sm text-ink-soft">Loading boards…</p>;

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Boards">
      {(levels.data?.boards ?? []).map(({ code: board, label }) => {
        const on = value.has(board);
        return (
          <label
            key={board}
            className="flex cursor-pointer items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition"
            style={{
              borderColor: on ? 'var(--brand-ink)' : 'var(--border-default)',
              background: on ? 'var(--brand-lime-soft)' : 'var(--surface-card)',
              color: on ? 'var(--brand-ink)' : 'var(--ink-2)',
              opacity: disabled ? 0.6 : 1,
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={on}
              disabled={disabled}
              onChange={() => {
                const next = new Set(value);
                if (next.has(board)) next.delete(board);
                else next.add(board);
                onChange(next);
              }}
            />
            {board}
            {label !== board && (
              <span className="font-normal" style={{ color: 'var(--ink-3)' }}>
                {label}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
